'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface CartLine {
  qty: number;
  addedAt: string;
}

// IMPORTANT: cart shape MUST NOT include price. Submit payload (Chunk 4) sends
// { linkSku, qty } only; the server re-resolves all prices via f_account_pricing.
// See plan §3.7 — caching price client-side is a tampering hazard.
export type CartState = Record<string, CartLine>;

interface CartContextValue {
  items: CartState;
  totalQty: number;
  add: (linkSku: string, qty?: number) => void;
  remove: (linkSku: string) => void;
  setQty: (linkSku: string, qty: number) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

function storageKey(scopeKey: string | null) {
  return scopeKey ? `goldline:cart:${scopeKey}` : null;
}

export function CartProvider({
  scopeKey,
  children,
}: {
  scopeKey: string | null;
  children: ReactNode;
}) {
  const [items, setItems] = useState<CartState>({});

  // Hydrate from localStorage when scopeKey changes. setState in effect is the
  // right pattern here — we're syncing state from an external store (window.
  // localStorage) that isn't accessible during render. The synchronous
  // re-render is intentional and capped at one per scopeKey change.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    const key = storageKey(scopeKey);
    if (!key) {
      setItems({});
      return;
    }
    try {
      const raw = window.localStorage.getItem(key);
      setItems(raw ? (JSON.parse(raw) as CartState) : {});
    } catch {
      setItems({});
    }
  }, [scopeKey]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Persist to localStorage on change
  useEffect(() => {
    const key = storageKey(scopeKey);
    if (!key) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(items));
    } catch {
      // quota or disabled storage — silently swallow
    }
  }, [items, scopeKey]);

  const add = useCallback((linkSku: string, qty: number = 1) => {
    setItems((prev) => {
      const existing = prev[linkSku];
      const next: CartLine = {
        qty: (existing?.qty ?? 0) + qty,
        addedAt: existing?.addedAt ?? new Date().toISOString(),
      };
      return { ...prev, [linkSku]: next };
    });
  }, []);

  const remove = useCallback((linkSku: string) => {
    setItems((prev) => {
      const next = { ...prev };
      delete next[linkSku];
      return next;
    });
  }, []);

  const setQty = useCallback((linkSku: string, qty: number) => {
    setItems((prev) => {
      if (qty <= 0) {
        const next = { ...prev };
        delete next[linkSku];
        return next;
      }
      const existing = prev[linkSku];
      return {
        ...prev,
        [linkSku]: { qty, addedAt: existing?.addedAt ?? new Date().toISOString() },
      };
    });
  }, []);

  const clear = useCallback(() => setItems({}), []);

  const totalQty = useMemo(() => Object.values(items).reduce((s, l) => s + l.qty, 0), [items]);

  const value: CartContextValue = useMemo(
    () => ({ items, totalQty, add, remove, setQty, clear }),
    [items, totalQty, add, remove, setQty, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used inside <CartProvider>');
  }
  return ctx;
}
